import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Loader2, CheckCircle, AlertCircle, FileText, X, Lock, ChevronLeft, ChevronRight, Check, ShieldCheck, Download } from 'lucide-react';
import { logger } from '../utils/logger';
import { API_URL, resolveAssetUrl, validateFieldValue } from '../components/booking/bookingUtils';
import { ClientHeader, ClientStepper, ClientSection, ReviewSummary } from '../components/booking/ClientFormParts';
import { SignaturePad } from '../components/booking/SignaturePad';
import { downloadPublicBookingPdf } from '../utils/bookingPdf';

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-4">
    <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-md p-8 text-center">{children}</div>
  </div>
);

export default function PublicBookingFormPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [formData, setFormData] = useState({});
  const [acknowledged, setAcknowledged] = useState(false);
  const [signature, setSignature] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitErrors, setSubmitErrors] = useState([]);
  const [step, setStep] = useState(0);

  const fetchBookingForm = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/bookings/${token}`);
      if (response.status === 404) return setError('This booking link is invalid or has expired.');
      if (response.status === 410) return setError('This booking link has expired. Please contact us for a new link.');
      if (!response.ok) throw new Error('Failed to load booking form');
      const data = await response.json();
      setBookingData(data);
      const seeded = {};
      (data.schema?.sections || []).forEach(s => (s.fields || []).forEach(f => {
        if (f.is_office_only) return;
        const v = data.client_responses?.[f.field_id] ?? data.office_responses?.[f.field_id];
        if (v !== undefined) seeded[f.field_id] = v;
      }));
      setFormData(seeded);
      if (data.is_locked) setSubmitted(true);
    } catch (err) {
      logger.error('Error fetching booking form:', err);
      setError('Failed to load booking form. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchBookingForm(); }, [fetchBookingForm]);

  const schema = bookingData?.schema || {};
  const sections = useMemo(() => bookingData?.schema?.sections || [], [bookingData]);
  const uploads = schema.required_uploads || [];
  const declaration = schema.declaration?.declaration_text ? schema.declaration : null;
  const officeResponses = bookingData?.office_responses || {};

  const steps = useMemo(() => {
    const list = sections.map((s, i) => ({ key: s.section_id, kind: 'section', name: s.title || `Section ${i + 1}`, index: i }));
    if (uploads.length) list.push({ key: 'uploads', kind: 'uploads', name: 'Documents' });
    if (declaration) list.push({ key: 'declaration', kind: 'declaration', name: 'Declaration' });
    list.push({ key: 'review', kind: 'review', name: 'Review & Submit' });
    return list;
  }, [sections, uploads.length, declaration]);

  const current = steps[step];
  const isLast = step === steps.length - 1;
  const locked = submitted || bookingData?.is_locked;

  const setField = (id, value) => { setSubmitErrors([]); setFormData(prev => ({ ...prev, [id]: value })); };

  const validateStep = () => {
    if (current.kind === 'section') {
      for (const field of sections[current.index].fields || []) {
        if (field.is_office_only) continue;
        const problem = validateFieldValue(field, formData[field.field_id]);
        if (problem) { toast.error(problem); return false; }
      }
    }
    if (current.kind === 'uploads') {
      for (const u of uploads) {
        if (u.required && !uploadedFiles[u.upload_id]) { toast.error(`Please upload "${u.label}"`); return false; }
      }
    }
    if (current.kind === 'declaration') {
      if (declaration.require_ack_checkbox && !acknowledged) { toast.error('Please acknowledge the declaration'); return false; }
      if (declaration.require_signature && !signature) { toast.error('Please provide your signature'); return false; }
    }
    return true;
  };

  const next = () => { if (validateStep()) { setStep(s => Math.min(s + 1, steps.length - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); } };
  const prev = () => { setStep(s => Math.max(s - 1, 0)); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const handleFileUpload = async (uploadId, file) => {
    if (!file) return;
    const body = new FormData();
    body.append('file', file);
    body.append('upload_id', uploadId);
    try {
      const response = await fetch(`${API_URL}/api/public/bookings/${token}/upload`, { method: 'POST', body });
      if (!response.ok) throw new Error((await response.json()).detail || 'Upload failed');
      const data = await response.json();
      setUploadedFiles(prev => ({ ...prev, [uploadId]: { ...data, name: file.name } }));
      toast.success('File uploaded successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to upload file');
    }
  };

  const collectAllErrors = () => {
    const list = [];
    sections.forEach((s, idx) => (s.fields || []).forEach(f => {
      if (f.is_office_only) return;
      const problem = validateFieldValue(f, formData[f.field_id]);
      if (problem) list.push({ message: problem, stepIndex: idx });
    }));
    const uploadsStep = steps.findIndex(st => st.kind === 'uploads');
    uploads.forEach(u => { if (u.required && !uploadedFiles[u.upload_id]) list.push({ message: `Please upload "${u.label}"`, stepIndex: uploadsStep }); });
    const declStep = steps.findIndex(st => st.kind === 'declaration');
    if (declaration?.require_ack_checkbox && !acknowledged) list.push({ message: 'Please acknowledge the declaration', stepIndex: declStep });
    if (declaration?.require_signature && !signature) list.push({ message: 'Please provide your signature', stepIndex: declStep });
    return list;
  };

  const handleSubmit = async () => {
    if (locked) return toast.error('This form has already been submitted');
    const localErrors = collectAllErrors();
    if (localErrors.length) { setSubmitErrors(localErrors); toast.error(localErrors[0].message); return; }
    const clientOnly = {};
    sections.forEach(s => (s.fields || []).forEach(f => { if (!f.is_office_only && formData[f.field_id] !== undefined) clientOnly[f.field_id] = formData[f.field_id]; }));
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/public/bookings/${token}/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_responses: clientOnly, declaration_acknowledged: acknowledged, signature_data: signature }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const serverErrors = errData.detail?.errors;
        if (Array.isArray(serverErrors) && serverErrors.length) {
          const stepForMessage = (msg) => sections.findIndex(s => (s.fields || []).some(f => msg.includes(`'${f.label}'`) || msg.includes(`"${f.label}"`)));
          setSubmitErrors(serverErrors.map(m => ({ message: m, stepIndex: stepForMessage(m) })));
          throw new Error('Some answers need attention — see the list above the Submit button');
        }
        throw new Error(typeof errData.detail === 'string' ? errData.detail : `Submission failed (${response.status}). Please try again.`);
      }
      setSubmitErrors([]);
      setSubmitted(true);
      setBookingData(prev => ({ ...prev, is_locked: true }));
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(err.message || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Shell><Loader2 className="w-8 h-8 animate-spin text-[#D4AF37] mx-auto mb-4" /><p className="text-slate-600">Loading your booking form...</p></Shell>;
  }
  if (error) {
    return <Shell><AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" /><h2 className="font-heading text-2xl mb-2">Unable to Load Form</h2><p className="text-slate-600">{error}</p></Shell>;
  }

  const { branding, template_name, customer_name, expires_at, booking_id } = bookingData;
  const logoSrc = resolveAssetUrl(branding?.logo_url);

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <ClientHeader branding={branding} logoSrc={logoSrc} templateName={template_name} bookingId={booking_id} customerName={customer_name} />
        <div className="max-w-xl mx-auto px-4 pt-12">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-md p-8 sm:p-10 text-center space-y-4" data-testid="submission-success">
            <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto"><CheckCircle className="w-9 h-9" /></div>
            <h2 className="font-heading text-2xl sm:text-3xl text-slate-900">{bookingData.is_locked && !signature ? 'This form has been submitted' : 'Form Submitted Successfully'}</h2>
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed">Thank you{customer_name ? `, ${customer_name}` : ''}. Our team will review your submission and contact you shortly. Please keep your reference number for future correspondence.</p>
            <p className="font-mono text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg inline-block px-4 py-2">Ref {booking_id}</p>
            <div className="pt-2">
              <Button onClick={() => downloadPublicBookingPdf(token)} className="h-11 px-6 bg-[#0F172A] hover:bg-[#1E293B] text-white gap-2" data-testid="client-download-pdf-btn">
                <Download className="w-4 h-4" /> {bookingData.company_signed ? 'Download signed copy of your booking form' : 'Download a copy of your booking form'}
              </Button>
              <p className="text-xs text-slate-500 mt-2">
                {bookingData.company_signed ? 'Signed by both parties — this is your official booking confirmation.' : 'The company signature will be added once our team confirms your booking; you can return to this link to download the signed copy.'}
              </p>
            </div>
            <p className="text-xs text-slate-400 pt-2 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> This form is now locked. Contact us if you need to make changes.</p>
          </div>
          <p className="text-center text-xs text-slate-400 mt-8">{branding?.watermark_text || branding?.company_name}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] text-slate-900 pb-16 antialiased" data-testid="public-booking-form">
      <ClientHeader branding={branding} logoSrc={logoSrc} templateName={template_name} bookingId={booking_id} customerName={customer_name} />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 space-y-6">
        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>Step {step + 1} of {steps.length}</span>
            <span className="font-medium text-slate-700">{current.name}</span>
          </div>
          <Progress value={((step + 1) / steps.length) * 100} className="h-1.5 bg-slate-200 [&>div]:bg-[#D4AF37]" />
          <ClientStepper steps={steps} current={step} onJump={setStep} />
        </div>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500 px-1" data-testid="client-legend">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-slate-200 border border-slate-300" /><Lock className="w-3 h-3" /> Pre-filled by our office (read-only)</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-white border-2 border-[#D4AF37]" /> Your response required</span>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-md p-6 sm:p-10 space-y-8 relative">
          {current.kind === 'section' && (
            <ClientSection section={sections[current.index]} index={current.index} formData={formData} officeResponses={officeResponses} onChange={setField} disabled={locked} />
          )}

          {current.kind === 'uploads' && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-2xl text-slate-900">Required Documents</h2>
                <p className="text-slate-500 text-sm mt-1">Please upload the following documents (Max 10MB each, JPG/PNG/PDF).</p>
              </div>
              {uploads.map(u => (
                <div key={u.upload_id} className="rounded-xl border border-[#D4AF37]/40 bg-white p-4 sm:p-5 space-y-2" data-testid={`upload-row-${u.upload_id}`}>
                  <Label className="text-sm font-semibold text-slate-900">{u.label}{u.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
                  {u.description && <p className="text-xs text-slate-500">{u.description}</p>}
                  {uploadedFiles[u.upload_id] ? (
                    <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <FileText className="w-5 h-5 text-emerald-600" />
                      <span className="flex-1 text-sm text-emerald-800 truncate">{uploadedFiles[u.upload_id].name}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setUploadedFiles(prev => { const n = { ...prev }; delete n[u.upload_id]; return n; })}><X className="w-4 h-4" /></Button>
                    </div>
                  ) : (
                    <Input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={(e) => handleFileUpload(u.upload_id, e.target.files[0])} data-testid={`upload-input-${u.upload_id}`}
                      className="h-11 file:mr-4 file:h-full file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[#D4AF37]/10 file:text-[#856404] hover:file:bg-[#D4AF37]/20" />
                  )}
                </div>
              ))}
            </div>
          )}

          {current.kind === 'declaration' && (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-6 h-6 text-[#D4AF37]" />
                <h2 className="font-heading text-2xl text-slate-900">Declaration</h2>
              </div>
              <div className="p-5 bg-[#FCFCFB] rounded-xl text-sm leading-relaxed whitespace-pre-wrap max-h-72 overflow-y-auto border border-slate-200" data-testid="declaration-text">{declaration.declaration_text}</div>
              {declaration.require_ack_checkbox && (
                <label className={`flex items-start gap-3 cursor-pointer p-4 rounded-xl border-2 transition-colors ${acknowledged ? 'border-[#D4AF37] bg-[#FDFBF7]' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} className="h-5 w-5 mt-0.5 rounded border-slate-300 accent-[#D4AF37]" data-testid="declaration-ack-checkbox" />
                  <span className="text-sm leading-relaxed">I have read and understood the above declaration and agree to the terms.</span>
                </label>
              )}
              {declaration.require_signature && (
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Your Signature <span className="text-red-500">*</span></Label>
                  <SignaturePad onChange={setSignature} />
                </div>
              )}
            </div>
          )}

          {current.kind === 'review' && (
            <>
              <ReviewSummary sections={sections} formData={formData} officeResponses={officeResponses} uploads={uploads} uploadedFiles={uploadedFiles}
                declaration={declaration} acknowledged={acknowledged} signature={signature} onJump={setStep} />
              {submitErrors.length > 0 && (
                <div className="rounded-xl border-2 border-red-200 bg-red-50 p-4 space-y-2" role="alert" data-testid="submit-errors-panel">
                  <p className="text-sm font-semibold text-red-800 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> Please correct the following before submitting:</p>
                  <ul className="space-y-1.5">
                    {submitErrors.map((err, i) => (
                      <li key={i} className="flex items-start justify-between gap-3 text-sm text-red-700" data-testid={`submit-error-${i}`}>
                        <span>• {err.message}</span>
                        {err.stepIndex >= 0 && (
                          <button type="button" onClick={() => { setStep(err.stepIndex); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-xs font-semibold underline text-red-800 shrink-0" data-testid={`submit-error-fix-${i}`}>Fix</button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          <div className="flex justify-between items-center pt-6 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={prev} disabled={step === 0} className="gap-1.5 h-11" data-testid="client-prev-btn">
              <ChevronLeft className="w-4 h-4" /> Previous
            </Button>
            {!isLast ? (
              <Button type="button" onClick={next} className="h-11 px-6 bg-[#0F172A] hover:bg-[#1E293B] text-white gap-1.5" data-testid="client-next-btn">
                Continue <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button type="button" onClick={handleSubmit} disabled={submitting || locked} className="h-11 px-6 bg-[#D4AF37] hover:bg-[#B5952F] text-[#0F172A] hover:text-white font-bold gap-1.5" data-testid="client-submit-btn">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <><Check className="w-4 h-4" /> Submit Form</>}
              </Button>
            )}
          </div>
        </div>

        <footer className="text-center text-xs text-slate-400 space-y-1 pt-4">
          <p className="font-medium text-slate-500">{branding?.watermark_text || branding?.company_name || 'Goldsands Group Inc'}</p>
          {expires_at && <p>This link expires on {new Date(expires_at).toLocaleDateString()}</p>}
        </footer>
      </div>
    </div>
  );
}
